// Copyright (c) 2025. CYBERKOALA LLC.
//
// SPDX-License-Identifier: Proprietary License.
//
// Redistribution and use in source and binary forms, with or without
// modification, are restricted.

import { useTranslation } from 'react-i18next';
import { ActionTooltip } from '../action-tooltip';
import { useNavigate } from 'react-router';
import { useEffect, useRef, useState } from 'react';
import { Badge } from '@heroui/react';
import { API_BASE } from '@/config.ts';
import { useGlobalSFX } from '@/context/sfx-context/use-sfx.ts';
import { useSettings } from '@/context/settings-context/settings-context.ts';
import { toast } from 'sonner';
import { DirectMessage, User } from '@/models';
import { INCOMING_CALL_PREFIX } from '@/context/call-context/call-state.ts';
import { useDmCallStore } from '@/stores/use-dm-call-store.ts';
import { useAuth } from '@/context/auth-context/use-auth.ts';
import { useModal } from '@/stores/use-modal-store.ts';
import { CyberKoalaCallsRoundedIcon } from '../icons/cyberkoala-calls-rounded-icon';

type UnreadCountData = {
  unreadCount: number;
  latestDirectMessages: DirectMessage[];
};
/**
 * TODO: after accepting conversation, update counter
 * @constructor
 */
export const NavigationCyberKoalaCalls = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const prevUnreadRef = useRef(0);
  const sfx = useGlobalSFX();
  const { notifications } = useSettings();
  const { openCall } = useDmCallStore();
  const { user: sessionUser } = useAuth();
  const { onOpen } = useModal();

  useEffect(() => {
    if (!('Notification' in window)) return;
    try {
      if (Notification.permission === 'default') {
        Notification.requestPermission();
      }
    } catch (error) {
      console.error(error);
    }
  }, []);

  useEffect(() => {
    let isSubscribed = true;

    const fetchUnreadCount = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/direct-messages/unread-count`, {
          credentials: 'include',
        });
        const data: UnreadCountData = await response.json();

        if (!isSubscribed || typeof data.unreadCount !== 'number') return;

        const currentPrev = prevUnreadRef.current;

        if (data.unreadCount > currentPrev) {
          const diff = data.unreadCount - currentPrev;
          const latestDirectMessage = data.latestDirectMessages[0];

          if (latestDirectMessage.content.startsWith(INCOMING_CALL_PREFIX)) {
            const conversationId = latestDirectMessage.content.slice(INCOMING_CALL_PREFIX.length);

            const resp = await fetch(`${API_BASE}/api/conversations/${conversationId}`, {
              credentials: 'include',
            });
            const convo = await resp.json();

            const other: User = convo.userOneId === sessionUser?.id ? convo.userTwo : convo.userOne;

            onOpen('incomingCall', {
              conversationId: conversationId,
              userName: other?.name || 'Неизвестный пользователь',
              avatarUrl: other?.image,
              userId: other?.id,
            });
            // TODO: translate
            toast('Входящий звонок', {
              action: {
                label: 'Принять',
                onClick: () => {
                  navigate(`/channels/@me/${other?.id}`);
                  openCall(conversationId, '');
                },
              },
            });
          } else {
            // TODO: dont spam with 3 notifications
            showNewMessageNotification(diff);
          }
        }

        prevUnreadRef.current = data.unreadCount;
        setUnreadCount(data.unreadCount);
      } catch (err) {
        console.error('Failed to fetch unread count:', err);
      }
    };

    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30_000);
    return () => {
      isSubscribed = false;
      clearInterval(interval);
    };
  }, []);

  /**
   * @param diff
   */
  const showNewMessageNotification = (diff: number) => {
    const title =
      diff === 1
        ? 'Одно непрочитанное личное сообщение'
        : `Новые непрочитанные личные сообщения (${diff})`;

    if (!notifications) return;

    toast('Личное сообщение', {
      description: title,
      action: {
        label: 'Перейти в ЛС',
        onClick: () => {
          navigate(`/channels/@me/friends`);
        },
      },
    });
    if (sfx?.newMessage) sfx.newMessage.play();
  };

  return (
    <ActionTooltip side="right" align="center" label={t('NavigationTalksy.tooltip')}>
      <button
        onClick={() => navigate(`/channels/@me/friends`)}
        className="group relative flex items-center"
        type="button"
      >
        <div className="mx-3 flex size-[48px] items-center justify-center overflow-hidden rounded-[24px] bg-background transition-all group-hover:rounded-[16px] group-hover:bg-white dark:bg-neutral-700">
          <CyberKoalaCallsRoundedIcon
            className="transition  grayscale group-hover:grayscale-0 group-hover:text-white group-hover:filter-none"
            width={48}
            height={48}
          />
        </div>
        {unreadCount > 0 && <Badge content={unreadCount} className="absolute right-3 -top-3" />}
      </button>
    </ActionTooltip>
  );
};
